import React, {
  useContext, useEffect, useState, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Layout, Table, notification, Row, Col, Card, Statistic, Select,
} from 'antd';
import { API } from 'aws-amplify';
import moment from 'moment';
import { Decimal } from 'decimal.js';
import PageHeaderBar from '../../components/PageHeaderBar';
import DateRangePicker from '../../components/DateRangePicker';
import Context from '../../context';
import CapacitySubscriptionChargeChart from './CapacitySubscriptionChargeChart';
import './index.less';
import { calculateWithUnit } from '../../utils/balanceGroupStatsHelpers';
import { formatNumber } from '../../utils/i18n';
import { UNITS_EXTENDED } from '../../constants/units';

const CapacitySubscriptionCharge = () => {
  const { t } = useTranslation();
  const { selectedMarketPartyId } = useContext(Context);

  const [data, setData] = useState([]); // store raw values (kWh)
  const [loading, setLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(UNITS_EXTENDED.MWH); // UI toggle
  const unitLabel = selectedUnit === UNITS_EXTENDED.MWH ? t('common.label.mwhPerHour') : t('common.label.kwhPerHour');
  const [peakInfo, setPeakInfo] = useState({ value: 0, timestamp: null, subscription: 0 });
  const [range, setRange] = useState([
    moment().startOf('month'),
    moment().endOf('month'),
  ]);

  const fetchData = async (marketPartyId, start, end) => {
    if (!marketPartyId || marketPartyId === 'admin') return;

    setLoading(true);
    try {
      const response = await API.get('FINTSO', `/marketparties/${marketPartyId}/capacity-subscription-charge/summary`, {
        queryStringParameters: {
          start: start.format('YYYY-MM-DD'),
          end: end.format('YYYY-MM-DD'),
          resolution: 'hour',
        },
      });

      const chartData = (response.values || [])
        .map((row) => ({
          timestamp: new Decimal(row[0] || 0).mul(1000).toNumber(),
          allocatedEnergy: new Decimal(row[1] || 0).toNumber(),
          capacitySubscription: new Decimal(row[2] || 0).toNumber(),
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      const maxObj = chartData.reduce((max, d) => (
        new Decimal(d.allocatedEnergy).gt(max.value)
          ? { value: d.allocatedEnergy, timestamp: d.timestamp, subscription: d.capacitySubscription }
          : max
      ), chartData[0] ? {
        value: chartData[0].allocatedEnergy,
        timestamp: chartData[0].timestamp,
        subscription: chartData[0].capacitySubscription,
      } : { value: 0, timestamp: null, subscription: 0 });

      setPeakInfo(maxObj);
      setData(chartData.map(d => ({ ...d, tehohuippu: maxObj.value })));
    } catch (error) {
      notification.error({
        message: t('common.notifications.errorFetching'),
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedMarketPartyId, range[0], range[1]);
  }, [selectedMarketPartyId, range]);

  // show `-` when value is null/undefined; show `0` when numeric zero
  const renderNumberOrDash = (rawVal) => {
    if (rawVal == null || rawVal === '') return '-';
    const converted = calculateWithUnit(selectedUnit, rawVal);
    if (!converted) return '-';
    const num = converted.toNumber();
    return (Number.isNaN(num)) ? '-' : formatNumber(num === 0 ? 0 : num, 0, 3);
  };

  const formattedTimestamp = useMemo(() => {
    if (!peakInfo.timestamp) return '-';
    const m = moment(peakInfo.timestamp);
    return m.isValid() ? m.format('DD.MM.YYYY HH:mm') : '-';
  }, [peakInfo.timestamp]);

  const columns = useMemo(() => ([
    {
      title: t('capacitySubscriptionCharge.table.timestampTable'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (ts) => {
        if (!ts) return '-';
        const m = moment(ts);
        return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '-';
      },
      sorter: (a, b) => a.timestamp - b.timestamp,
    },
    {
      title: `${t('capacitySubscriptionCharge.table.allocatedEnergyTable')} (${unitLabel})`,
      dataIndex: 'allocatedEnergy',
      key: 'allocatedEnergy',
      align: 'right',
      sorter: (a, b) => a.allocatedEnergy - b.allocatedEnergy,
      render: (val) => renderNumberOrDash(val),
    },
    {
      title: `${t('capacitySubscriptionCharge.table.capacitySubscription')} (${unitLabel})`,
      dataIndex: 'capacitySubscription',
      key: 'capacitySubscription',
      align: 'right',
      render: (val) => renderNumberOrDash(val),
    },
    {
      title: `${t('capacitySubscriptionCharge.table.charge')} (${unitLabel})`,
      dataIndex: 'tehohuippu',
      key: 'tehohuippu',
      align: 'right',
      render: (val) => renderNumberOrDash(val),
    },
  ]), [t, selectedUnit, unitLabel]);

  return (
    <div className="capacity-subscription-charge">
      <PageHeaderBar
        title={t('capacitySubscriptionCharge.title')}
        span={{ title: 12, action: 12 }}
        actionItems={<></>}
      />
      <Layout.Content className="layout__container__page__content">
        <div style={{
          marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center',
        }}
        >
          <DateRangePicker
            defaultStart={range[0]}
            defaultEnd={range[1]}
            onValidDateSelected={(start, end) => setRange([start, end])}
          />
          <div className="unit-selector">
            <div className="title">{t('common.label.unit')}</div>
            <Select
              defaultValue={selectedUnit}
              value={selectedUnit}
              onChange={setSelectedUnit}
              style={{ width: 120 }}
            >
              <Select.Option value={UNITS_EXTENDED.KWH}>{t('common.label.kwhPerHour')}</Select.Option>
              <Select.Option value={UNITS_EXTENDED.MWH}>{t('common.label.mwhPerHour')}</Select.Option>
            </Select>
          </div>
        </div>
        <Card style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={t('common.table.header.timePeriod')}
                value={`${range[0].format('DD.MM.YYYY')} - ${range[1].format('DD.MM.YYYY')}`}
                valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={t('capacitySubscriptionCharge.table.charge')}
                value={renderNumberOrDash(peakInfo.value)}
                suffix={unitLabel}
                valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="ant-statistic">
                <div className="ant-statistic-title">{t('capacitySubscriptionCharge.table.timestamp')}</div>
                <div className="ant-statistic-content" style={{ fontSize: '16px' }}>
                  <span className="ant-statistic-content-value" style={{ fontWeight: 'bold' }}>{formattedTimestamp}</span>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={t('capacitySubscriptionCharge.table.capacitySubscription')}
                value={renderNumberOrDash(peakInfo.subscription)}
                suffix={unitLabel}
                valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
              />
            </Col>
          </Row>
        </Card>

        <div style={{ marginBottom: '24px', background: '#fff', padding: '24px' }}>
          {useMemo(() => (
            <CapacitySubscriptionChargeChart data={data} selectedUnit={selectedUnit} />
          ), [data, selectedUnit])}
        </div>
        <Table
          dataSource={data}
          columns={columns}
          loading={loading}
          rowKey="timestamp"
          pagination={{ pageSize: 24 }}
        />
      </Layout.Content>
    </div>
  );
};

export default CapacitySubscriptionCharge;
